import { Head, Link } from "@inertiajs/react"
import { Download } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type ApplicationRow = {
  id: number
  first_name: string
  last_name: string
  email: string
  qualified: boolean
  booked_at: string | null
  podcast_situation: string
  created_at: string
}

const SITUATION_LABELS: Record<string, string> = {
  launch: "Ready to launch",
  scale: "Wants to scale",
  guidance: "Needs guidance",
  overwhelmed: "Overwhelmed",
}

export default function AdminApplicationsIndex({
  applications,
}: {
  applications: ApplicationRow[]
}) {
  return (
    <>
      <Head title="Applications">
        <meta name="description" content="Fit Call applications submitted through the site." />
        <meta property="og:title" content="Applications" />
        <meta property="og:description" content="Fit Call applications submitted through the site." />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Applications</h1>
              <p className="mt-1">
                {applications.length}{" "}
                {applications.length === 1 ? "application" : "applications"} submitted.
              </p>
            </div>
            {applications.length > 0 && (
              <Button asChild variant="secondary">
                {/* Plain anchor so the browser downloads the CSV file. */}
                <a href="/admin/applications/export">
                  <Download className="h-4 w-4" />
                  Export CSV
                </a>
              </Button>
            )}
          </div>
        </div>

        {applications.length === 0 ? (
          <p className="mt-10 text-ink-muted">No applications yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-md border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Name</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Email</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Fit</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Situation</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Submitted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {applications.map((a) => (
                  <tr key={a.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/applications/${a.id}`}
                        className="font-medium text-ink-display no-underline hover:text-accent-display"
                      >
                        {a.first_name} {a.last_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`mailto:${a.email}`} className="text-ink-body no-underline hover:text-accent-display">
                        {a.email}
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex flex-wrap gap-1.5">
                        {a.qualified ? (
                          <Badge tone="accent">Qualified</Badge>
                        ) : (
                          <Badge tone="muted">Not yet</Badge>
                        )}
                        {a.booked_at && <Badge tone="signal">Booked</Badge>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-muted">
                      {SITUATION_LABELS[a.podcast_situation] ?? a.podcast_situation}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-muted">
                      {formatDate(a.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
